/**
 * Plugin Event Bus
 *
 * Enables event-driven communication between plugins and the host application.
 * Supports pub/sub pattern with async event handlers.
 *
 * Features:
 * - Type-safe event handling
 * - Multiple subscribers per event type
 * - Async event handlers
 * - Automatic error handling
 * - Unsubscribe support
 */

import { PluginEventBus as IPluginEventBus, PluginEvent } from '@/lib/platform/types/plugins';

import { pluginLogger } from '@/lib/platform/utils/logger';

type EventHandler = (event: PluginEvent) => void | Promise<void>;

export class PluginEventBus implements IPluginEventBus {
  private listeners = new Map<string, Set<EventHandler>>();
  private debug: boolean;

  constructor(debug: boolean = false) {
    this.debug = debug;
  }

  /**
   * Subscribe to an event type
   * @param eventType Event type to listen for (e.g., 'valuation.created')
   * @param handler Function to call when event is emitted
   * @returns Unsubscribe function
   */
  subscribe(eventType: string, handler: EventHandler): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    this.listeners.get(eventType)!.add(handler);

    if (this.debug) {
      pluginLogger.log(
        `[PluginEventBus] Subscribed to "${eventType}". Total listeners: ${this.listeners.get(eventType)!.size}`
      );
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(eventType, handler);
    };
  }

  /**
   * Unsubscribe from an event type
   */
  private unsubscribe(eventType: string, handler: EventHandler): void {
    const handlers = this.listeners.get(eventType);

    if (handlers) {
      handlers.delete(handler);

      if (this.debug) {
        pluginLogger.log(
          `[PluginEventBus] Unsubscribed from "${eventType}". Remaining listeners: ${handlers.size}`
        );
      }

      // Clean up empty sets
      if (handlers.size === 0) {
        this.listeners.delete(eventType);
      }
    }
  }

  /**
   * Emit an event to all subscribers
   * All handlers are executed in parallel
   * Errors in handlers don't prevent other handlers from running
   */
  async emit(event: PluginEvent): Promise<void> {
    const handlers = this.listeners.get(event.type);

    if (!handlers || handlers.size === 0) {
      if (this.debug) {
        pluginLogger.log(`[PluginEventBus] No listeners for event "${event.type}"`);
      }
      return;
    }

    if (this.debug) {
      pluginLogger.log(`[PluginEventBus] Emitting "${event.type}" to ${handlers.size} listeners`);
    }

    // Execute all handlers in parallel
    const results = await Promise.allSettled(
      Array.from(handlers).map((handler) => Promise.resolve(handler(event)))
    );

    // Log any errors
    const errors = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];

    if (errors.length > 0) {
      pluginLogger.error(
        `[PluginEventBus] ${errors.length} handler(s) failed for event "${event.type}":`,
        errors.map((e) => e.reason)
      );
    }
  }

  /**
   * Get number of subscribers for an event type
   */
  getSubscriberCount(eventType: string): number {
    return this.listeners.get(eventType)?.size || 0;
  }

  /**
   * Get all event types that have subscribers
   */
  getEventTypes(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Clear all subscribers
   */
  clearAll(): void {
    this.listeners.clear();

    if (this.debug) {
      pluginLogger.log('[PluginEventBus] Cleared all subscribers');
    }
  }

  /**
   * Clear subscribers for a specific event type
   */
  clearEventType(eventType: string): void {
    this.listeners.delete(eventType);

    if (this.debug) {
      pluginLogger.log(`[PluginEventBus] Cleared subscribers for "${eventType}"`);
    }
  }

  /**
   * Get statistics
   */
  getStats(): {
    totalEventTypes: number;
    totalSubscribers: number;
    eventTypes: { type: string; subscribers: number }[];
  } {
    const eventTypes = Array.from(this.listeners.entries()).map(([type, handlers]) => ({
      type,
      subscribers: handlers.size,
    }));

    return {
      totalEventTypes: this.listeners.size,
      totalSubscribers: eventTypes.reduce((sum, e) => sum + e.subscribers, 0),
      eventTypes,
    };
  }

  /**
   * Enable/disable debug logging
   */
  setDebug(enabled: boolean): void {
    this.debug = enabled;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a typed event emitter for specific event types
 */
export function createTypedEmitter<T extends PluginEvent>(
  eventBus: PluginEventBus
): {
  emit: (event: T) => Promise<void>;
  subscribe: (eventType: T['type'], handler: (event: T) => void | Promise<void>) => () => void;
} {
  return {
    emit: (event: T) => eventBus.emit(event),
    subscribe: (eventType: T['type'], handler: (event: T) => void | Promise<void>) =>
      eventBus.subscribe(eventType, handler as EventHandler),
  };
}

/**
 * Debounce event emissions
 */
export function debounceEvent(
  eventBus: PluginEventBus,
  eventType: string,
  delayMs: number
): (event: PluginEvent) => void {
  let timeoutId: NodeJS.Timeout | null = null;

  return (event: PluginEvent) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      eventBus.emit(event);
      timeoutId = null;
    }, delayMs);
  };
}

/**
 * Throttle event emissions
 */
export function throttleEvent(
  eventBus: PluginEventBus,
  eventType: string,
  delayMs: number
): (event: PluginEvent) => void {
  let lastEmit = 0;
  let timeoutId: NodeJS.Timeout | null = null;

  return (event: PluginEvent) => {
    const now = Date.now();

    if (now - lastEmit >= delayMs) {
      eventBus.emit(event);
      lastEmit = now;
    } else if (!timeoutId) {
      timeoutId = setTimeout(
        () => {
          eventBus.emit(event);
          lastEmit = Date.now();
          timeoutId = null;
        },
        delayMs - (now - lastEmit)
      );
    }
  };
}
